import {Component, attr} from "../Component";
import {element} from "../CustomElement";
import {i18n, friendlyValidity} from "../../i18n/I18n";
import {icons} from "../../icons/Icons";
import {isCrawler} from "../../utils/crawler";

import {Link} from "../../components/flipp-link/Link";
import {LazyImage} from "../../components/flipp-lazy-image/LazyImage";

import {inject} from "../../services/di";
import {SeoService} from "../../services/SeoService";
import {Flyer} from "../../definitions/FlyerDefinitions";
import {AnalyticsService} from "../../services/AnalyticsService";
import {
	LandingPageActions,
	FavouriteContext,
	AggregateSourceAction,
	SourceAction,
	getCategorySourceActionValue
} from "../../services/definitions/IAnalyticsService";
import {FlyerService} from "../../services/FlyerService";
import {FavouriteService} from "../../services/FavouriteService";

import { LocationService } from "../../services/LocationService";
import {Rect} from "../../utils/Rect";
import { Timer } from "../../utils/Timer";


export enum FlyerListingItemContext {
	Listing,
	LandingPage,
	Favourites,
	Search
}


export interface FlyerListingItemProps extends JSX.HTMLProps {
	flyerId: number;
	context: FlyerListingItemContext;
	premium?: boolean;
	favourite?: boolean;
	category?: string;
}

@element("flipp-flyer-listing-item")
export class FlyerListingItem extends Component<FlyerListingItemProps, Flyer> {

	@inject flyerService: FlyerService;
	@inject favourites: FavouriteService;
	@inject analytics: AnalyticsService;
	@inject seo: SeoService;
	@inject location: LocationService;

	@attr flyerId: number;
	@attr context: FlyerListingItemContext;
	@attr premium: boolean;
	@attr category: string = "All Flyers";

	refs: {
		spinner: HTMLElement,
		flyerContainer: HTMLElement
	};

	private isFavourite: boolean = false;
	private newFavourite: boolean = false;
	private hasFavouriteChanged: boolean = false;
	private isDisplayed: boolean = false;
	private scrollDebouncer = new Timer(10, this.loadImageIfInViewport, this);

	prepare() {
		this.loadImageIfInViewport();
		return this.flyerService.getFlyer(this.flyerId, false, true).then((flyer: Flyer) => {
			this.isFavourite = this.favourites.isFavourite(flyer);
			return flyer;
		});
	}

	onAttach() {
		this.loadImageIfInViewport();
		window.addEventListener("scroll", this.handleViewportChange, true);
		window.addEventListener("resize", this.handleViewportChange, true);
		window.addEventListener("flipp-favourites", this._handleFavouritesChanged);
	}

	onDetach() {
		window.removeEventListener("flipp-favourites", this._handleFavouritesChanged);
	}

	onRender() {
		this.newFavourite = false;
	}

	render(flyer: Flyer) {
		const attrs: { [key: string]: string } = {};
		if (this.newFavourite) {
			attrs["pulse"] = `${true}`;
		}

		let sourceAction: AggregateSourceAction = SourceAction.BrowseFlyerClick;

		switch (this.context) {
			case FlyerListingItemContext.Listing: {
				if (this.category === "All Flyers") {
					sourceAction = SourceAction.BrowseFlyerClick;
				} else {
					const name = this.category
						? this.category
							.toLowerCase()
							.trim()
							.replace(/[ ]+/g, "_")
						: "all_flyers";

					sourceAction = getCategorySourceActionValue(name);
				}
				break;
			}
			case FlyerListingItemContext.Favourites:
				sourceAction = SourceAction.FavoriteClick;
				break;

			case FlyerListingItemContext.Search:
				sourceAction = SourceAction.SearchFlyerClick;
				break;

			default:
				sourceAction = SourceAction.BrowseFlyerClick;
				break;
		}

		if (!this.isDisplayed && !isCrawler()) {
			return null;
		}

		if (this.premium) {
			return (
				<div>
					{ !isCrawler() ? <section ref="spinner" class="spinner-wrapper show">
						<div class="loading">
							<div class="spinner"></div>
						</div>
					</section> : null}
					<Link
						ref="flyerContainer"
						class="premium-flyer-container"
						classList={{favourite: this.isFavourite, hidden: !isCrawler() && !this.hasFavouriteChanged}}
						to={flyer}
						onClick={this._handleLandingPageFlyerOpen}
						sourceAction={sourceAction}
					>
						<FlyerFavouriteButton flyerId={this.flyerId} context={this.context} {...attrs} />
						<LazyImage
							class="flyer-thumbnail"
							alt={this.getFlyersAltText(flyer)} fit cover
							dataSrc={flyer.thumbnail}
							onload={!isCrawler() ? this.hideImageSpinner : null}/>
						<div class="flyer-info">
							<LazyImage
								class="logo"
								alt=""
								fit
								contain
								dataSrc={flyer.merchantLogo}
							/>
							<div class="flyer-info-block">
								<p class="flyer-name">{this.getFlyerName(flyer)}</p>
								<p>{friendlyValidity(flyer.validFrom, flyer.validTo)}</p>
							</div>
						</div>
					</Link>
				</div>
			);
		} else {
			return (
				<div>
					{ !isCrawler() ? <section ref="spinner" class="spinner-wrapper non-premium show">
						<div class="loading">
							<div class="spinner"></div>
						</div>
					</section> : null}
					<Link
						ref="flyerContainer"
						class="flyer-container"
						classList={{favourite: this.isFavourite, hidden: !isCrawler() && !this.hasFavouriteChanged}}
						to={flyer}
						sourceAction={sourceAction}
					>
						<FlyerFavouriteButton flyerId={this.flyerId} context={this.context} {...attrs} />
						<LazyImage
							class="flyer-thumbnail"
							alt={this.getFlyersAltText(flyer)}
							fit cover
							dataSrc={flyer.thumbnail}
							onload={!isCrawler() ? this.hideImageSpinner : null}/>
						<p class="flyer-name">{this.getFlyerName(flyer)}</p>
						<p>{friendlyValidity(flyer.validFrom, flyer.validTo)}</p>
					</Link>
				</div>
			);
		}
	}

	private handleViewportChange = () => {
		this.scrollDebouncer.reset();
	}

	private isInViewport() {
		const screenRect = new Rect(0, 0, window.innerWidth, window.innerHeight);
		const boundingRect = this.getBoundingClientRect();

		return Rect.intersects(screenRect, boundingRect);
	}

	private loadImageIfInViewport() {
		if (isCrawler()) {
			return;
		}
		const browser = navigator.userAgent;

		if (this.attached && this.isInViewport()) {
			const objectFit = this.style.hasOwnProperty("objectFit") &&
					this.style.hasOwnProperty("objectPosition");
			if (objectFit || browser.includes("Firefox") || (browser.includes("Trident") || browser.includes("Edge"))) {
				this.isDisplayed = true;
				this.invalidate();
			}

			this.unbindEvents();
		}
	}

	private unbindEvents() {
		window.removeEventListener("scroll", this.handleViewportChange, true);
		window.removeEventListener("resize", this.handleViewportChange, true);
	}

	private _handleFavouritesChanged = () => {
		const newVal = this.favourites.isFavourite(this.model);
		const changed = newVal !== this.isFavourite;
		if (newVal && !this.isFavourite) {
			// Item was just favourited. Track it so we can play the heart pulse!
			this.newFavourite = true;
		}
		this.isFavourite = newVal;
		if (changed) {
			this.hasFavouriteChanged = true;
			this.invalidate();
		}
	}

	// FIXME_ivan: need to revisit this for merchant landing page and Items landing page.
	private _handleLandingPageFlyerOpen = (e: Event) => {
		if (this.context === FlyerListingItemContext.LandingPage) {
			this.analytics.trackLandingPage(LandingPageActions.FlyerOpen);
		}
	}

	private getFlyerName = (flyer: Flyer) => {
		return `${flyer.merchant} ${flyer.name}`;
	}
	private getFlyersAltText = (flyer: Flyer) => {
		const city = this.location.city;
		const flyerName = this.getFlyerName(flyer);

		if (this.category !== "All Flyers") {
			return i18n("category_flyers_alt", {
				city,
				flyerName,
				category: i18n(this.category),
			});
		}

		return i18n("index_flyers_alt", {
			city,
			flyerName
		});
	}

	private hideImageSpinner = () => {
		this.refs.spinner.classList.remove("show");
		this.refs.flyerContainer.classList.remove("hidden");
	}
}

// --- flipp-flyer-favourite-button ------------------------------>

interface FlyerFavouriteButtonProps {
	flyerId: number;
	context: FlyerListingItemContext;
}

@element("flipp-flyer-favourite-button")
class FlyerFavouriteButton extends Component<FlyerFavouriteButtonProps, Flyer> {

	@inject flyerService: FlyerService;
	@inject favourites: FavouriteService;
	@inject analytics: AnalyticsService;

	@attr flyerId: number;
	@attr context: FlyerListingItemContext;

	prepare() {
		if (!isCrawler()) {
			return this.flyerService.getFlyer(this.flyerId, false);
		}
	}

	render(flyer: Flyer) {
		if (!isCrawler()) {
			return <button aria-label={i18n("fav_page")} ref="heart" onClick={this._toggleFavourite}>
				<div flex-horizontal>
					{this.favourites.isFavourite(flyer) ?
						<img class="full-heart" src="https://f.wishabi.net/creative/Distribution/images/icons/fullHeart.svg" alt=""/>
						:
						<img class="empty-heart" src="https://f.wishabi.net/creative/Distribution/images/icons/emptyHeart.svg" alt=""/>}
				</div>
			</button>;
		}
	}

	private _toggleFavourite = (evt: Event) => {
		evt.preventDefault();
		evt.stopPropagation();
		if (this.favourites.isFavourite(this.model)) {
			this.favourites.removeFavourite(this.model);
		} else {
			this.favourites.addFavourite(this.model);
		}

		this.analytics.trackFavouriteEvent(this.model.merchantId,
			this.favourites.isFavourite(this.model) ? "Favorited" : "Unfavorited",
			this._getFavouriteContext(this.context));
	}

	private _getFavouriteContext = (context: FlyerListingItemContext) => {
		switch (context) {
			case 0:
				return FavouriteContext.Listing;
			case 2:
				return FavouriteContext.Favourites;
			case 3:
				return FavouriteContext.Search;
			case 1:
			default:
				return null; /* N/A (Landing page) */
		}
	}
}

// --- flipp-tombstone-listing-item --------------------------------------->

export interface TombstoneListingItemProps extends JSX.HTMLAttributes {
	merchantLogo: string;
}

@element("flipp-tombstone-listing-item")
export class TombstoneListingItem extends Component<TombstoneListingItemProps, {}> {
	@attr merchantLogo: string;

	render() {
		return (
			<div class="flyer-container">
				<div class="tombstone-img-container">
					<img class="tombstone-thumbnail" src={this.merchantLogo} alt="" contain/>
				</div>
				<div class="tombstone-msg">{i18n("tombstone_msg")}</div>
			</div>
		);
	}
}
